import { WeekBreakdownItem } from '../../models/stats.models';
import { formatChartHoursTick, secondsToChartHours } from '../../pages/training-log/training-log.helpers';

export { formatChartHoursTick };

export type TimeRangeFilter = 'all' | 'p1-6' | 'p7-13';

export type CoreLayerKey = 'volume' | 'weeklyDistance' | 'cumulative';

export type ChartLayerKey = CoreLayerKey | string;

export const CORE_LAYER_CONFIG: Record<CoreLayerKey, { label: string; color: string; dataKey: string }> = {
  volume: { label: 'Weekly Volume', color: '#2563EB', dataKey: 'volume' },
  weeklyDistance: { label: 'Weekly Distance', color: '#BFDBFE', dataKey: 'weeklyDistance' },
  cumulative: { label: 'Cumulative Hours', color: '#B4A3F0', dataKey: 'cumulative' },
};

export const filterWeeksByTimeRange = (weeks: WeekBreakdownItem[], timeRange: TimeRangeFilter): WeekBreakdownItem[] => {
  if (timeRange === 'all') {
    return weeks;
  }
  if (timeRange === 'p1-6') {
    return weeks.filter((week) => week.period <= 6);
  }
  return weeks.filter((week) => week.period >= 7);
};

export const getSportKeysFromBreakdown = (weeks: WeekBreakdownItem[]): string[] => {
  const keys = new Set<string>();
  weeks.forEach((week) => {
    Object.keys(week.sport ?? {}).forEach((key) => keys.add(key));
  });
  return Array.from(keys).sort();
};

export interface ChartRow {
  week: string;
  weekLabel: string;
  volume: number;
  weeklyDistance: number;
  cumulative: number;
  [sportKey: string]: string | number;
}

export const buildChartData = (weeks: WeekBreakdownItem[], sportKeys: string[]): ChartRow[] => {
  return weeks.map((week) => {
    const row: ChartRow = {
      week: week.week,
      weekLabel: week.week,
      volume: secondsToChartHours(week.duration?.total ?? 0),
      weeklyDistance: week.distance?.total ?? 0,
      cumulative: secondsToChartHours(week.duration?.cumulative ?? 0),
    };

    sportKeys.forEach((sportKey) => {
      row[sportKey] = secondsToChartHours(week.sport?.[sportKey]?.duration ?? 0);
    });

    return row;
  });
};

export const getPeriodSplitLabel = (periodCount: number): { first: string; second: string } | null => {
  if (periodCount <= 6) {
    return null;
  }
  return {
    first: 'Periods 1–6',
    second: `Periods 7–${periodCount}`,
  };
};

const niceCeil = (value: number, step: number): number => {
  if (value <= 0) {
    return step;
  }
  return Math.ceil(value / step) * step;
};

export const getDistanceAxisDomain = (distances: number[]): [number, number] => {
  const max = Math.max(...distances, 0);
  if (max === 0) {
    return [0, 100];
  }
  const step = max <= 50 ? 10 : max <= 150 ? 20 : 50;
  return [0, niceCeil(max * 1.1, step)];
};

export const getDurationAxisDomain = (hours: number[]): [number, number] => {
  const max = Math.max(...hours, 0);
  if (max === 0) {
    return [0, 5];
  }
  return [0, niceCeil(max * 1.15, 5)];
};

export const getCumulativeAxisDomain = (hours: number[]): [number, number] => {
  const max = Math.max(...hours, 0);
  if (max === 0) {
    return [0, 100];
  }
  const step = max <= 100 ? 20 : max <= 300 ? 50 : 100;
  return [0, niceCeil(max * 1.05, step)];
};
